import axios from 'axios';
import { API_BASE_URL } from 'configs/AppConfig';
import { signOutSuccess } from 'store/slices/authSlice';
import store from '../store';
import { AUTH_TOKEN } from 'constants/AuthConstant';
import { notification } from 'antd';

const service = axios.create({
  baseURL: API_BASE_URL,
  headers:
    process.env.NODE_ENV === 'development'
      ? {
          'ngrok-skip-browser-warning': '1',
        }
      : {},
});

// Config

// API Request interceptor
service.interceptors.request.use(
  (config) => {
    const jwtToken = localStorage.getItem(AUTH_TOKEN) || null;

    if (jwtToken) {
      config.headers.Authorization = `Bearer ${jwtToken}`;
    }

    return config;
  },
  (error) => {
    // Do something with request error here
    notification.error({
      message: 'Error',
    });
    Promise.reject(error);
  }
);

// API respone interceptor
service.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    const status = error.response?.status;
    // Backend error bodies are not uniform: most send { message }, auth
    // middlewares send { error } and projectOwnership sends { error, detail }.
    const backendMessage =
      error.response?.data?.message ||
      error.response?.data?.detail ||
      error.response?.data?.error;
    const requestUrl = error.config?.url || '';
    // The login OTP endpoints are public — a 401/403/404 there means the code
    // or email was wrong (or the account isn't a merchant), NOT that an
    // existing session is invalid. The login page renders these errors inline,
    // so show no toast and skip the sign-out side effects entirely.
    const isOtpLoginRequest =
      requestUrl.includes('signInWithOTPViaEmail') ||
      requestUrl.includes('sendOTPViaEmail');

    if (isOtpLoginRequest) {
      return Promise.reject(error);
    }

    let notificationParam = {
      message: backendMessage || error?.message || 'Unknown error',
    };

    if (status === 401) {
      // 401 on a portal API = session expired OR this account isn't linked to
      // a merchant (requireMerchant). Make that actionable instead of "Unauthorized".
      notificationParam.message =
        "Your session expired or this account isn't authorised for the merchant portal. Please log in with your registered merchant email, or contact Sotravel.";
      // Fixed key so parallel failing requests collapse into a single toast.
      notificationParam.key = 'session-error';
      localStorage.removeItem(AUTH_TOKEN);
      store.dispatch(signOutSuccess());
    } else if (status === 403) {
      // Preserve the backend's explanation (e.g. account deleted / forbidden)
      // and sign out, mirroring the original behaviour.
      notificationParam.message = backendMessage || 'Forbidden';
      notificationParam.key = 'session-error';
      localStorage.removeItem(AUTH_TOKEN);
      store.dispatch(signOutSuccess());
    }

    if (status === 404) {
      notificationParam.message = 'Not Found';
    }

    if (status === 500) {
      notificationParam.message = 'Internal Server Error';
    }

    if (status === 508) {
      notificationParam.message = 'Time Out';
    }

    // Callers that render their own error UI (e.g. the trip-builder save flow:
    // confirmation modals for 409 guard responses, a toast for real failures)
    // opt out of the global popup via `silentError` in the request config so
    // the user sees exactly one message. Auth side effects above still run,
    // and 401 keeps its notification so a signed-out user learns why.
    const suppressNotification = Boolean(error.config?.silentError) && status !== 401;
    if (!suppressNotification) {
      notification.error(notificationParam);
    }

    return Promise.reject(error);
  }
);

export default service;
